import { take, call, put } from 'redux-saga/effects'
import * as types from '../../../../lib/actionTypes.es6'
import * as actions from './actions'

import {
  getPolicyProcessingDcp,
  policyActions,
  policyRejectActions,
  decideMakerCheck,
  returnToPriorStage,
  policyNotes,
  updatePoliciesDcp,
  cancelV2Policy,
  deletePolicyDocument,
  getPaymentByPolicyGuid,
  initiatePolicyMobileMoneyPayment,
  generatePolicyCheckoutLink,
  findCustomerForTransfer,
  switchPolicyCustomer,
  getCustomerQuotes,
  confirmQuotation,
  rejectQuotation
} from '../../../../lib/requests.es6'

import { GAloggerException } from '../../../../lib/monitoring.es6'
import { unAuthorizedNotification } from '../../../../lib/access.es6'

export function* getPolicyProcessingDcpRequestWatcher() {
  for (;;) {
    const request = yield take(types.GET_POLICY_PROCESSING_DCP_REQUEST)
    try {
      const response = yield call(getPolicyProcessingDcp, request.payload)
      yield put(actions.getPolicyProcessingDcpSuccess(response.data))
    } catch (e) {
      const errorPayload = {
        message: e.response ? e.response.data : 'An unexpected error occurred',
        status: e.response ? e.response.status : null
      }
      yield put(actions.getPolicyProcessingDcpFailure(errorPayload))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* getPolicyProcessingCustomerQuotesRequestWatcher() {
  for (;;) {
    const request = yield take(
      types.GET_POLICY_PROCESSING_CUSTOMER_QUOTES_REQUEST
    )
    try {
      const response = yield call(getCustomerQuotes, request.payload)
      yield put(actions.getPolicyProcessingCustomerQuotesSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.getPolicyProcessingCustomerQuotesFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

// A policy waiting on a maker-check (AccessBank's single-button flow) is
// decided through the check; anything else is the plain status update. See
// lib/singleDecisionMakerCheck.
export const policyStatusWrite = update => payload =>
  payload.makerCheckDecision
    ? decideMakerCheck({
        guid: payload.policy_guid,
        body: payload.makerCheckDecision
      })
    : update(payload)

export function* policyActionsRequestWatcher() {
  for (;;) {
    const request = yield take(types.POLICY_ACTION_REQUEST)
    try {
      const response = yield call(
        policyStatusWrite(policyActions),
        request.payload
      )
      yield put(actions.policyActionSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.policyActionFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* policyRejectActionsRequestWatcher() {
  for (;;) {
    const request = yield take(types.POLICY_REJECT_ACTION_REQUEST)
    try {
      const response = yield call(
        policyStatusWrite(policyRejectActions),
        request.payload
      )
      yield put(actions.policyRejectActionSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.policyRejectActionFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* makerCheckDecisionRequestWatcher() {
  for (;;) {
    const request = yield take(types.MAKER_CHECK_DECISION_REQUEST)
    try {
      const response = yield call(decideMakerCheck, request.payload)
      // Issue 1: persist a stage rejection's reason to the policy notes. The
      // decisions endpoint records the decision only; the note is a separate
      // write, mirroring cancelPolicyActionRequestWatcher below. Only fires
      // once the decision itself has succeeded.
      if (request.payload.note) {
        yield put(actions.addPolicyNotesRequest(request.payload.note))
      }
      // Issue 2 (option B): mirror the AML Check stage into the aml_flagged
      // flag the AML review queue filters on. Guarded so a flag-sync failure
      // never fails an already-recorded decision.
      if (request.payload.amlFlagUpdate) {
        try {
          yield call(updatePoliciesDcp, request.payload.amlFlagUpdate)
        } catch (flagError) {
          GAloggerException(flagError)
        }
      }
      yield put(actions.makerCheckDecisionSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.makerCheckDecisionFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* returnToPriorStageRequestWatcher() {
  for (;;) {
    const request = yield take(types.RETURN_TO_PRIOR_STAGE_REQUEST)
    try {
      const response = yield call(returnToPriorStage, request.payload)
      if (request.payload.note) {
        yield put(actions.addPolicyNotesRequest(request.payload.note))
      }
      yield put(actions.returnToPriorStageSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.returnToPriorStageFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

// The "needs changes" round trip: flip additional_details.need_action, then
// record the reason as a policy note.
//
// The two writes are sequenced, not fired in parallel. Dispatched
// independently they could half-succeed — the flag moving the policy between
// queues while the note explaining why never lands, or the note appearing on a
// policy that never moved — and neither failure surfaced anywhere. The note is
// therefore attempted only once the flag write has succeeded, mirroring
// makerCheckDecisionRequestWatcher above.
//
// A failed note does not fail the handoff. The flag is the thing that moves the
// policy and it is already written by then; reporting failure would tell the
// user nothing happened when in fact the policy has moved. It is logged
// instead, matching how the AML flag sync is guarded above.
export function* setNeedActionRequestWatcher() {
  for (;;) {
    const request = yield take(types.SET_NEED_ACTION_REQUEST)
    try {
      const response = yield call(policyActions, request.payload.policyUpdate)

      if (request.payload.note) {
        try {
          yield call(policyNotes, request.payload.note)
        } catch (noteError) {
          GAloggerException(noteError)
        }
      }

      yield put(actions.setNeedActionSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.setNeedActionFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* quotationDecisionRequestWatcher() {
  for (;;) {
    const request = yield take(types.QUOTATION_DECISION_REQUEST)
    try {
      const decide =
        request.payload.decision === 'reject'
          ? rejectQuotation
          : confirmQuotation
      const response = yield call(decide, { guid: request.payload.guid })
      yield put(actions.quotationDecisionSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.quotationDecisionFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* cancelPolicyActionRequestWatcher() {
  for (;;) {
    const request = yield take(types.CANCEL_POLICY_ACTION_REQUEST)
    try {
      const response = yield call(cancelV2Policy, request.payload.policy.guid)
      yield put(actions.addPolicyNotesRequest(request.payload.note))
      yield put(actions.cancelPolicyActionSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.cancelPolicyActionFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* addPolicyNotesAddRequestWatcher() {
  for (;;) {
    const request = yield take(types.ADD_POLICY_NOTES_REQUEST)
    try {
      const response = yield call(policyNotes, request.payload)
      yield put(actions.addPolicyNotesSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.addPolicyNotesFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* deleteCustomerPolicyDocumentRequestWatcher() {
  while (true) {
    try {
      const { payload } = yield take(types.DELETE_POLICY_DOCUMENT_REQUEST)

      const response = yield call(deletePolicyDocument, payload)
      yield put(actions.deletepolicyDocumentSuccess(response.data))
    } catch (e) {
      let errorPayload = {
        message: 'An error occurred, this could be due to invalid data'
      }
      if (e.response) {
        errorPayload = e.response.data
        unAuthorizedNotification(e)
      }
      yield put(actions.deletepolicyDocumentFailed(errorPayload))
      GAloggerException(e)
    }
  }
}

export function* getPaymentsByPolicyGuidRequestWatcher() {
  for (;;) {
    const request = yield take(types.GET_PAYMENTS_BY_POLICY_GUID_REQUEST)
    try {
      const response = yield call(getPaymentByPolicyGuid, request.payload)
      yield put(actions.getPaymentsByPolicyGuidSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.getPaymentsByPolicyGuidFailure(errorMessage))

      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* initiatePaymentWatcher() {
  for (;;) {
    try {
      const { payload } = yield take(types.INITIATE_PAYMENT_REQUEST)
      const response = yield call(initiatePolicyMobileMoneyPayment, payload)
      let responseData = response.data
      if (responseData === '') responseData = 'success'
      yield put(actions.initiatePaymentSuccess(responseData))
    } catch (e) {
      let errorPayload = { message: 'Ooops something went wrong' }
      if (e.response) {
        errorPayload = e.response.data
        unAuthorizedNotification(e)
      }
      GAloggerException(e)
      yield put(actions.initiatePaymentFailure(errorPayload))
    }
  }
}

export function* generatePolicyCheckoutLinkWatcher() {
  for (;;) {
    const { payload } = yield take(types.GENERATE_POLICY_CHECKOUT_LINK_REQUEST)
    try {
      const response = yield call(generatePolicyCheckoutLink, payload)
      const data = (response && response.data) || {}
      // Paystack's field name, nested where the backend happens to have put it.
      const url =
        (data.additional_details &&
          data.additional_details.authorization_url) ||
        (data.request_response &&
          data.request_response.data &&
          data.request_response.data.authorization_url)
      if (url && data.status !== 'Failed') {
        yield put(actions.generatePolicyCheckoutLinkSuccess(url))
      } else {
        yield put(
          actions.generatePolicyCheckoutLinkFailure({
            message:
              data.message ||
              "We couldn't start this payment. Please try again."
          })
        )
      }
    } catch (e) {
      let errorPayload = { message: 'Ooops something went wrong' }
      if (e.response) {
        errorPayload = e.response.data
        unAuthorizedNotification(e)
      }
      GAloggerException(e)
      yield put(actions.generatePolicyCheckoutLinkFailure(errorPayload))
    }
  }
}

export function* findCustomerForTransferRequestWatcher() {
  for (;;) {
    const request = yield take(types.FIND_CUSTOMER_FOR_TRANSFER_REQUEST)
    try {
      const response = yield call(findCustomerForTransfer, request.payload)
      yield put(actions.findCustomerForTransferSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.findCustomerForTransferFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* switchPolicyCustomerRequestWatcher() {
  for (;;) {
    const request = yield take(types.SWITCH_POLICY_CUSTOMER_REQUEST)
    try {
      const response = yield call(switchPolicyCustomer, request.payload)
      yield put(actions.switchPolicyCustomerSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response) {
        errorMessage = e.response.data
      }
      yield put(actions.switchPolicyCustomerFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}
